import { useState, useEffect, useCallback, useRef } from "react";
import {
  getDraft,
  saveDraft,
  removeDraft,
  getDraftKeys as getStorageDraftKeys,
} from "@/infrastructure/storage";

interface UseFormDraftOptions<T> {
  /** Unique key for this draft (e.g., "expense-session123") */
  key: string;
  /** Initial form data */
  initialData: T;
  /** Debounce delay in ms (default: 500) */
  debounceMs?: number;
  /** Whether auto-save is enabled (default: true) */
  enabled?: boolean;
}

interface UseFormDraftReturn<T> {
  /** Current form data */
  data: T;
  /** Update form data (triggers auto-save) */
  setData: (data: T | ((prev: T) => T)) => void;
  /** Whether there's a saved draft */
  hasDraft: boolean;
  /** Restore draft data */
  restoreDraft: () => void;
  /** Clear the draft */
  clearDraft: () => void;
  /** Save status: "idle" | "saving" | "saved" */
  saveStatus: "idle" | "saving" | "saved";
  /** Reset to initial data and clear draft */
  reset: () => void;
}

/**
 * Hook for auto-saving form data to localStorage with debounce.
 * Helps users recover from interruptions (train exit, meetings, etc.)
 */
export function useFormDraft<T>({
  key,
  initialData,
  debounceMs = 500,
  enabled = true,
}: UseFormDraftOptions<T>): UseFormDraftReturn<T> {
  const [data, setDataInternal] = useState<T>(initialData);
  const [hasDraft, setHasDraft] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef(true);

  const prevEnabledRef = useRef(enabled);

  // Check for existing draft and re-read when enabled transitions to true
  useEffect(() => {
    const wasDisabled = !prevEnabledRef.current;
    prevEnabledRef.current = enabled;

    if (!enabled) return;

    const result = getDraft<T>(key);
    if (result.data !== null) {
      setHasDraft(result.hasContent);

      // If transitioning from disabled to enabled, reload data from storage
      if (wasDisabled && result.hasContent) {
        setDataInternal(result.data);
        isInitialMount.current = true; // Prevent immediate auto-save
      }
    } else {
      setHasDraft(false);
      // If transitioning from disabled to enabled with no draft, reset to initial
      if (wasDisabled) {
        setDataInternal(initialData);
        isInitialMount.current = true;
      }
    }
  }, [key, enabled, initialData]);

  // Auto-save with debounce
  useEffect(() => {
    if (!enabled) return;

    // Skip initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    setSaveStatus("saving");

    debounceRef.current = setTimeout(() => {
      const saved = saveDraft(key, data);
      setHasDraft(saved);
      setSaveStatus("saved");

      // Reset to idle after showing "saved"
      setTimeout(() => setSaveStatus("idle"), 1500);
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [data, key, debounceMs, enabled]);

  const setData = useCallback((newData: T | ((prev: T) => T)) => {
    setDataInternal(newData);
  }, []);

  const restoreDraft = useCallback(() => {
    if (!enabled) return;

    const result = getDraft<T>(key);
    if (result.data !== null) {
      setDataInternal(result.data);
    }
  }, [key, enabled]);

  const clearDraft = useCallback(() => {
    // Cancel any pending save
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    removeDraft(key);
    setHasDraft(false);
    setSaveStatus("idle");
    // Reset data to initial and prevent auto-save from triggering
    setDataInternal(initialData);
    isInitialMount.current = true;
  }, [key, initialData]);

  const reset = useCallback(() => {
    setDataInternal(initialData);
    clearDraft();
    isInitialMount.current = true;
  }, [initialData, clearDraft]);

  return {
    data,
    setData,
    hasDraft,
    restoreDraft,
    clearDraft,
    saveStatus,
    reset,
  };
}

/**
 * Get all draft keys for a given prefix.
 * Useful for showing a list of saved drafts.
 */
export { getStorageDraftKeys as getDraftKeys };

/**
 * Clear all drafts for a given prefix.
 */
export { clearDrafts } from "@/infrastructure/storage";
